import React, { useState, useEffect } from 'react';
import { Tabs } from '@/components/common/Tabs';
import { ToolModal } from '@/components/tools/ToolModal';
import { ToolSelector } from '@/components/tools/ToolSelector';
import { ProgressBar } from './ProgressBar';
import { useData } from '@/contexts/DataContext';
import { evaluationsApi } from '@/services/evaluations.api';
import { systemApi } from '@/services/system.api';
import { Agent, Tool, GradingScript, ToolInstance, CreateEvaluationDto } from '@/types';

interface CreateEvaluationProps {
  onEvaluationCreated: (id: string) => void;
}

export function CreateEvaluation({ onEvaluationCreated }: CreateEvaluationProps) {
  // Form state
  const [selectedAgent, setSelectedAgent] = useState('');
  const [configuredTools, setConfiguredTools] = useState<ToolInstance[]>([]);
  const [evaluationName, setEvaluationName] = useState('');
  const [selectedGradingScript, setSelectedGradingScript] = useState('');
  const [gradingFunction, setGradingFunction] = useState('grade');
  
  // Dataset state
  const [datasetInput, setDatasetInput] = useState('');
  const [uploadedDataset, setUploadedDataset] = useState<any[] | null>(null);
  const [selectedDatasetFile, setSelectedDatasetFile] = useState('');
  const [previewedDataset, setPreviewedDataset] = useState<any[] | null>(null);
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  
  // UI state
  const [activeDatasetTab, setActiveDatasetTab] = useState('json-input');
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);

  // Get preloaded data
  const { agents, tools, gradingScripts, refetchAgents, refetchTools, refetchGradingScripts } = useData();

  // Dataset example for placeholder
  const datasetExample = `[
  {
    "input": "What is 2+2?",
    "ground_truth": "4"
  },
  {
    "input": "What is the capital of France?",
    "ground_truth": "Paris"
  },
  {
    "input": {"text": "Complex input", "context": "Additional data"},
    "ground_truth": {"result": "Expected structured output"}
  }
]`;

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      setUploadedDataset(json);
      setError('');
    } catch {
      setError('Invalid JSON file');
      setUploadedDataset(null);
    }
  };

  // Handle existing file selection
  const handleExistingFilePreview = async () => {
    if (!selectedDatasetFile) return;

    try {
      const response = await systemApi.readFile(selectedDatasetFile);
      setPreviewedDataset(response.content);
      setError('');
    } catch {
      setError('Failed to load dataset file');
      setPreviewedDataset(null);
    }
  };

  // Auto-load dataset when file is selected
  const handleFileSelection = async (filePath: string) => {
    setSelectedDatasetFile(filePath);
    if (filePath) {
      setIsLoadingDataset(true);
      try {
        const response = await systemApi.readFile(filePath);
        setPreviewedDataset(response.content);
        setError('');
      } catch {
        setError('Failed to load dataset file');
        setPreviewedDataset(null);
      } finally {
        setIsLoadingDataset(false);
      }
    } else {
      setPreviewedDataset(null);
      setIsLoadingDataset(false);
    }
  };

  // Tool management
  const handleAddTool = (tool: ToolInstance) => {
    setConfiguredTools(prev => [...prev, tool]);
  };

  const handleRemoveTool = (index: number) => {
    setConfiguredTools(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditTool = (index: number) => {
    // For now, just remove and let user re-add
    handleRemoveTool(index);
    setIsToolModalOpen(true);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }

    // Get dataset based on active method
    let dataset: any[] | null = null;

    if (activeDatasetTab === 'json-input') {
      if (!datasetInput.trim()) {
        setError('Please enter evaluation data');
        return;
      }
      try {
        dataset = JSON.parse(datasetInput);
      } catch {
        setError('Invalid JSON in dataset input');
        return;
      }
    } else if (activeDatasetTab === 'file-upload') {
      if (!uploadedDataset) {
        setError('Please upload a dataset file');
        return;
      }
      dataset = uploadedDataset;
    } else if (activeDatasetTab === 'existing-file') {
      if (!selectedDatasetFile) {
        setError('Please select a dataset file');
        return;
      }
      if (isLoadingDataset) {
        setError('Please wait for the dataset to finish loading');
        return;
      }
      if (!previewedDataset) {
        setError('Failed to load the selected dataset file');
        return;
      }
      dataset = previewedDataset;
    }

    if (!dataset || !Array.isArray(dataset) || dataset.length === 0) {
      setError('Dataset must be a non-empty array');
      return;
    }

    setIsSubmitting(true);
    setProgress({ current: 0, total: dataset.length });

    try {
      const payload: CreateEvaluationDto = {
        dataset,
        agent_path: selectedAgent,
        tools: configuredTools.length > 0 ? configuredTools : undefined,
        grading_script: selectedGradingScript || undefined,
        grading_function: gradingFunction || undefined,
        evaluation_name: evaluationName || undefined,
      };

      const response = await evaluationsApi.create(payload);
      setSuccess(`Evaluation started with ID: ${response.id}`);
      onEvaluationCreated(response.id);
      
      // Reset form
      setSelectedAgent('');
      setConfiguredTools([]);
      setEvaluationName('');
      setDatasetInput('');
      setUploadedDataset(null);
      setSelectedDatasetFile('');
      setPreviewedDataset(null);
      
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create evaluation');
      setProgress(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const datasetTabs = [
    {
      id: 'json-input',
      label: 'Direct JSON Input',
      content: (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Enter a JSON array of objects with <code className="bg-gray-100 px-1 rounded">input</code> and{' '}
            <code className="bg-gray-100 px-1 rounded">ground_truth</code> fields.
          </p>
          <textarea
            value={datasetInput}
            onChange={(e) => setDatasetInput(e.target.value)}
            placeholder={datasetExample}
            rows={12}
            className="w-full font-mono text-sm border border-gray-300 rounded-md p-3 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
          />
          {datasetInput.trim() && (
            <div className="mt-2">
              {(() => {
                try {
                  const parsed = JSON.parse(datasetInput);
                  const isValid = Array.isArray(parsed) && parsed.length > 0;
                  return (
                    <span className={`text-sm font-medium ${isValid ? 'text-green-700' : 'text-red-700'}`}>
                      {isValid ? `✓ Valid dataset (${parsed.length} items)` : '✗ Dataset must be a non-empty array'}
                    </span>
                  );
                } catch {
                  return <span className="text-sm font-medium text-red-700">✗ Invalid JSON syntax</span>;
                }
              })()}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'file-upload',
      label: 'Upload File',
      content: (
        <div>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {uploadedDataset && (
            <div className="mt-4">
              <div className="mb-2">
                <span className={`text-sm font-medium ${Array.isArray(uploadedDataset) && uploadedDataset.length > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {Array.isArray(uploadedDataset) && uploadedDataset.length > 0 
                    ? `✓ Valid dataset (${uploadedDataset.length} items)` 
                    : '✗ Dataset must be a non-empty array'}
                </span>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                <pre className="text-sm">{JSON.stringify(uploadedDataset, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'existing-file',
      label: 'Select Existing File',
      content: (
        <div>
          <div className="flex space-x-2 mb-4">
            <select
              value={selectedDatasetFile}
              onChange={(e) => handleFileSelection(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a dataset...</option>
              <option value="src/eval/part_time/eval_population.json">Part-time Eval Population</option>
              <option value="src/eval/examples/qa_eval.json">Q&A Examples</option>
              <option value="src/eval/examples/part_time_eval.json">Part-time Examples</option>
              <option value="src/eval/examples/classification_eval.json">Classification Examples</option>
            </select>
            <button
              onClick={handleExistingFilePreview}
              disabled={!selectedDatasetFile || isLoadingDataset}
              className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingDataset ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          {isLoadingDataset && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-center">
              <div className="inline-flex items-center text-blue-700">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                Loading dataset...
              </div>
            </div>
          )}
          {previewedDataset && !isLoadingDataset && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md max-h-48 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-700">✓ Dataset loaded ({Array.isArray(previewedDataset) ? previewedDataset.length : 0} items)</span>
              </div>
              <pre className="text-sm">{JSON.stringify(previewedDataset, null, 2)}</pre>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 border-b pb-4">New Evaluation</h2>
      
      <p className="text-gray-600 text-sm mb-6">
        Create a new evaluation by selecting an agent, providing test data, and optionally configuring tools and grading functions.
      </p>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Agent Selection */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">1. Select Agent</h3>
          <p className="text-sm text-gray-600 mb-4">Choose which AI agent to evaluate.</p>
          
          <div className="flex space-x-2">
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select an agent...</option>
              {agents.map((agent) => (
                <option key={agent.module_path} value={agent.module_path}>
                  {agent.name} ({agent.module_path})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={refetchAgents}
              className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* Tool Configuration */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">2. Configure Tools (Optional)</h3>
          <p className="text-sm text-gray-600 mb-4">
            Select and configure tools that your agent should have access to.
          </p>
          
          <div className="flex space-x-2 mb-4">
            <button
              type="button"
              onClick={() => setIsToolModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
            >
              + Add Tool
            </button>
            <button
              type="button"
              onClick={refetchTools}
              className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              ↻ Refresh Tools
            </button>
          </div>
          
          <ToolSelector
            tools={configuredTools}
            onRemoveTool={handleRemoveTool}
            onEditTool={handleEditTool}
          />
        </div>

        {/* Dataset Input */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">3. Choose Evaluation Data</h3>
          <Tabs 
            tabs={datasetTabs} 
            defaultTab="json-input" 
            onTabChange={setActiveDatasetTab}
          />
        </div>

        {/* Grading Configuration */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">4. Configure Grading (Optional)</h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose how to score the agent's outputs. Without a grading script, exact matches score 1.0, non-matches score 0.0.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grading Script
              </label>
              <div className="flex space-x-2">
                <select
                  value={selectedGradingScript}
                  onChange={(e) => setSelectedGradingScript(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">None (exact match)</option>
                  {gradingScripts.map((script) => (
                    <option key={script.path} value={script.path}>
                      {script.name} ({script.relative_path})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={refetchGradingScripts}
                  className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  ↻ Refresh
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grading Function Name
              </label>
              <input
                type="text"
                value={gradingFunction}
                onChange={(e) => setGradingFunction(e.target.value)}
                placeholder="grade"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Evaluation Options */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">5. Evaluation Options</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Evaluation Name (optional)
            </label>
            <input
              type="text"
              value={evaluationName}
              onChange={(e) => setEvaluationName(e.target.value)}
              placeholder="e.g., Q&A Performance Test"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              Give this evaluation run a descriptive name for easier identification.
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {progress && (
          <ProgressBar 
            progress={progress.current} 
            total={progress.total}
            label="Evaluation Progress"
          />
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 text-green-700">
            {success}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-700 focus:ring-4 focus:ring-green-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-lg"
        >
          {isSubmitting ? 'Starting Evaluation...' : 'Start Evaluation'}
        </button>
      </form>

      {/* Tool Modal */}
      <ToolModal
        isOpen={isToolModalOpen}
        onClose={() => setIsToolModalOpen(false)}
        tools={tools}
        onAddTool={handleAddTool}
      />
    </div>
  );
}